import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PillOption {
  value: string;
  label: string;
  /** A small color dot before the label (palette). */
  swatch?: React.ReactNode;
}

interface FacetPillsProps {
  label: string;
  options: PillOption[];
  values: string[];
  onChange(values: string[]): void;
  /** Options shown before "Show {n} more"; everything when unset. */
  collapsedCount?: number;
  /** The last selected value can't be turned off (a filter that needs at least one). */
  required?: boolean;
}

const PILL_IDLE = "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50";
const PILL_ACTIVE = "bg-slate-100 border-slate-400 text-slate-900";

/** Multi-select toggle pills; long lists start short and grow in place. */
export const FacetPills: React.FC<FacetPillsProps> = ({
  label,
  options,
  values,
  onChange,
  collapsedCount,
  required = false,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const collapsible = collapsedCount !== undefined && options.length > collapsedCount;
  // A picked value stays visible even while the list is short.
  const shown =
    !collapsible || expanded
      ? options
      : options.filter((option, i) => i < collapsedCount! || values.includes(option.value));
  const hiddenCount = options.length - shown.length;

  const toggle = (value: string) => {
    const on = values.includes(value);
    if (on && required && values.length === 1) return;
    onChange(on ? values.filter(v => v !== value) : [...values, value]);
  };

  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {shown.map(option => {
        const selected = values.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(option.value)}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors duration-200 cursor-pointer",
              selected ? PILL_ACTIVE : PILL_IDLE
            )}
          >
            {option.swatch}
            {option.label}
            <span
              className={cn(
                "grid transition-[grid-template-columns,opacity] duration-300 ease-out motion-reduce:transition-none",
                selected ? "grid-cols-[1fr] opacity-100" : "grid-cols-[0fr] opacity-0"
              )}
              aria-hidden="true"
            >
              <span className="min-w-0 overflow-hidden">
                <Check className="size-3" />
              </span>
            </span>
          </button>
        );
      })}
      {collapsible && (hiddenCount > 0 || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(on => !on)}
          className="inline-flex h-7 items-center rounded-full px-2 text-xs font-semibold text-primary hover:underline cursor-pointer"
        >
          {expanded ? "Show less" : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};
